// file: src/api/landlordApi.js
import {
  signInWithPersistence,
  getIdToken as fbGetIdToken,
  logout as fbLogout,
} from "../lib/firebaseCompact";

import {
  emitAuthExpired,
  clearAllAuth,
  hardRedirectToLogin,
} from "../auth/session";

export const API_BASE = "https://towing-portal-api.onrender.com";
export const LANDLORD_LOGIN_PATH = "/landlord/signin";

/* -------- Remember-me flag (UI only) -------- */
const L_REMEMBER = "landlord_remember_me";
const L_USER = "landlord_user";

/* -------- LocalStorage access token key --------
   Must match what you actually store (your screenshot shows "access_token") */
const LS_ACCESS_TOKEN = "access_token";

export function setLandlordRemember(v) {
  try {
    localStorage.setItem(L_REMEMBER, v ? "1" : "0");
  } catch {}
}
export function getLandlordRemember() {
  try {
    return localStorage.getItem(L_REMEMBER) === "1";
  } catch {
    return true;
  }
}
export function setLandlordUser(user) {
  try {
    localStorage.setItem(L_USER, JSON.stringify(user || null));
  } catch {}
}
export function getLandlordUser() {
  try {
    return JSON.parse(localStorage.getItem(L_USER) || "null");
  } catch {
    return null;
  }
}
export function clearLandlordAuth() {
  try {
    localStorage.removeItem(L_REMEMBER);
    localStorage.removeItem(L_USER);
  } catch {}
}

/* ---------- fetch core (with timeout) ---------- */
async function fetchWithTimeout(resource, options = {}) {
  const { timeout = 0, signal: extSignal, ...rest } = options;
  const controller = new AbortController();
  const onAbort = () => controller.abort(extSignal?.reason);

  if (extSignal) {
    if (extSignal.aborted) onAbort();
    else extSignal.addEventListener("abort", onAbort);
  }

  let id = null;
  if (Number.isFinite(timeout) && timeout > 0) {
    id = setTimeout(
      () => controller.abort(new DOMException("timeout", "TimeoutError")),
      timeout,
    );
  }

  try {
    return await fetch(resource, { ...rest, signal: controller.signal });
  } finally {
    if (id) clearTimeout(id);
    if (extSignal) extSignal.removeEventListener("abort", onAbort);
  }
}

async function parseOrText(res) {
  const ct = res.headers.get("content-type") || "";
  const isJson = ct.includes("application/json");
  return isJson ? await res.json().catch(() => ({})) : await res.text();
}

/* ============================================================
   A) Firebase header-auth fetch (keep for any endpoints that
      still need Firebase tokens)
   ============================================================ */
async function landlordAuthFetch(url, options = {}) {
  const { autoRedirectOn401 = true, retryOn401 = true, ...fetchOpts } = options;

  const headers = new Headers(fetchOpts.headers || {});
  if (!headers.has("accept")) headers.set("accept", "application/json");

  // 1) try current Firebase ID token
  let idToken = "";
  try {
    idToken = await fbGetIdToken(false);
  } catch {}
  if (idToken) headers.set("authorization", `Bearer ${idToken}`);

  let res = await fetchWithTimeout(url, {
    ...fetchOpts,
    credentials: "omit",
    headers,
  });

  // 2) retry once with fresh Firebase token
  if ((res.status === 401 || res.status === 403) && retryOn401) {
    try {
      const fresh = await fbGetIdToken(true);
      if (fresh && fresh !== idToken) {
        const h2 = new Headers(headers);
        h2.set("authorization", `Bearer ${fresh}`);
        res = await fetchWithTimeout(url, {
          ...fetchOpts,
          credentials: "omit",
          headers: h2,
        });
      }
    } catch {}
  }

  if (res.status !== 401 && res.status !== 403) return res;

  emitAuthExpired("landlord");
  if (autoRedirectOn401 !== false) {
    clearAllAuth();
    hardRedirectToLogin(LANDLORD_LOGIN_PATH);
  }
  return res;
}

async function apiGetL(url, opts = {}) {
  const res = await landlordAuthFetch(url, { method: "GET", ...opts });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

/* ============================================================
   B) LocalStorage header-auth fetch (ONLY for these 3 endpoints)
      - lookupPlateLandlord
      - getAllLandlordReservations
      - listDoNotTow
   ============================================================ */
function getLocalAccessToken() {
  try {
    return String(localStorage.getItem(LS_ACCESS_TOKEN) || "").trim();
  } catch {
    return "";
  }
}

async function landlordAuthFetchLocal(url, options = {}) {
  const { autoRedirectOn401 = true, ...fetchOpts } = options;

  const headers = new Headers(fetchOpts.headers || {});
  if (!headers.has("accept")) headers.set("accept", "application/json");

  const token = getLocalAccessToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetchWithTimeout(url, {
    ...fetchOpts,
    credentials: "omit",
    headers,
  });

  if (res.status !== 401 && res.status !== 403) return res;

  emitAuthExpired("landlord");
  if (autoRedirectOn401 !== false) {
    clearAllAuth();
    hardRedirectToLogin(LANDLORD_LOGIN_PATH);
  }
  return res;
}

async function apiGetLocal(url, opts = {}) {
  const res = await landlordAuthFetchLocal(url, { method: "GET", ...opts });
  const body = await parseOrText(res);
  if (!res.ok) {
    const err = new Error(
      (body && (body.message || body.error || body.detail)) ||
        `Request failed (${res.status})`,
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

/* ---------- Login ---------- */
/**
 * Keep Firebase sign-in (don’t disturb your working auth UI).
 * Backend call is bookkeeping only.
 */
export async function landlordLogin(
  { email, password, remember = true },
  opts = {},
) {
  const cred = await signInWithPersistence(email, password, remember);
  const idToken = await cred.user.getIdToken(true);

  const res = await fetch(`${API_BASE}/api/v1/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(opts.headers || {}),
    },
    body: JSON.stringify({ id_token: idToken, remember_me: !!remember }),
    signal: opts.signal,
  });

  const body = await parseOrText(res);
  if (!res.ok) {
    const msg = body?.message || `Login failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status || 401;
    err.payload = body;
    throw err;
  }

  // ✅ IMPORTANT: store backend token for local-auth endpoints
  if (body?.access_token) {
    localStorage.setItem(LS_ACCESS_TOKEN, body.access_token);
  } else if (body?.token) {
    // in case backend sends `token` instead of `access_token`
    localStorage.setItem(LS_ACCESS_TOKEN, body.token);
  }

  setLandlordRemember(!!remember);
  if (body?.user) setLandlordUser(body.user);

  return body;
}

/* ---------- Password reset ---------- */
export async function requestPasswordReset({ email, frontendUrl }) {
  const res = await fetch(`${API_BASE}/api/v1/auth/password-reset/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, frontend_url: frontendUrl }),
  });

  if (!res.ok) {
    let msg = `Reset request failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json?.();
}

export async function confirmPasswordReset({ email, token, newPassword }) {
  const res = await fetch(`${API_BASE}/api/v1/auth/password-reset/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, token, new_password: newPassword }),
  });

  if (!res.ok) {
    let msg = `Password reset failed (${res.status})`;
    try {
      const j = await res.json();
      msg = j?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json?.();
}

/* ---------- Landlord endpoints (LOCAL STORAGE token auth) ---------- */
export async function lookupPlateLandlord(plate_number, opts = {}) {
  const plate = String(plate_number || "")
    .toUpperCase()
    .replace(/\s+/g, "");
  if (!plate) {
    const e = new Error("plate_number is required");
    e.status = 400;
    throw e;
  }

  const url = `${API_BASE}/api/v1/lookup/plate/landlord/${encodeURIComponent(
    plate,
  )}`;

  // ✅ USE LOCAL STORAGE TOKEN
  return await apiGetLocal(url, {
    signal: opts.signal,
    timeout: 0,
    cache: "no-store",
  });
}

export async function getAllLandlordReservations(
  { date_from, date_to, limit = 1000, offset = 0, statuses = "valid" } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (date_from) qs.set("date_from", date_from);
  if (date_to) qs.set("date_to", date_to);
  if (Number.isFinite(limit))
    qs.set("limit", String(Math.min(1000, Math.max(1, limit))));
  if (Number.isFinite(offset)) qs.set("offset", String(Math.max(0, offset)));
  if (statuses) qs.set("statuses", String(statuses));

  const url = `${API_BASE}/api/v1/lookup/reservations/landlord?${qs.toString()}`;

  // ✅ USE LOCAL STORAGE TOKEN
  return await apiGetLocal(url, {
    signal: opts.signal,
    timeout: opts.timeout ?? 20000,
    cache: "no-store",
  });
}

/* Shared DNT endpoint (LOCAL STORAGE token auth) */
export async function listDoNotTow(
  { skip = 0, limit = 100, active_only = false, search } = {},
  opts = {},
) {
  const qs = new URLSearchParams();
  if (Number.isFinite(skip)) qs.set("skip", String(skip));
  if (Number.isFinite(limit)) qs.set("limit", String(limit));
  if (typeof active_only === "boolean")
    qs.set("active_only", String(active_only));
  if (search) qs.set("search", String(search));

  const url = `${API_BASE}/api/v1/do-not-tow/?${qs.toString()}`;

  // ✅ USE LOCAL STORAGE TOKEN
  return await apiGetLocal(url, {
    signal: opts.signal,
    timeout: opts.timeout ?? 15000,
    cache: "no-store",
  });
}

export default {
  landlordLogin,
  lookupPlateLandlord,
  getAllLandlordReservations,
  getLandlordUser,
  setLandlordRemember,
  getLandlordRemember,
  clearLandlordAuth,
  listDoNotTow,
};
